import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type { CrawlRunRef, RunComparisonState } from './url-run-comparison.service';

/**
 * Crawl history and site-level diff — FW-CRAWL-DELTA-01 phase 5.
 * Backend: `GET /api/v1/domains/:domain/crawl-runs`.
 *
 * These types mirror `backend/src/utils/crawl-run-site-diff.ts`. `state` carries
 * the same weight it does on a per-URL delta: a diff that is not `compared` has
 * no buckets at all, and rendering its counts would report a site rebuilt from
 * scratch — the population-sized version of the phantom delta
 * (MONITORING_CRAWL_DELTAS.md §4).
 */
export type SiteDiffBucket =
  | 'new'
  | 'removed'
  | 'reworked'
  | 'minor-change'
  | 'unchanged'
  | 'not-compared';

export interface CrawlRunSiteDiffEntry {
  urlId: string;
  url: string;
  bucket: SiteDiffBucket;
  /** Why this URL is `not-compared`. Null in every other bucket. */
  reason: string | null;
  /** False when the URL was `force_exclude`d at capture time. Runs capture
   *  excluded URLs too, so this separates an inventory decision from a change
   *  to the site. */
  tracked: boolean | null;
  wordCount: number | null;
  previousWordCount: number | null;
  wordCountChange: number | null;
  relativeWordCountChange: number | null;
  /** The canonical rendering the backend owns. The UI never subtracts. */
  display: string;
}

export interface CrawlRunSiteDiffBucketView {
  bucket: SiteDiffBucket;
  label: string;
  description: string;
  count: number;
  /** Capped by the backend; `count` is always the true total. */
  entries: CrawlRunSiteDiffEntry[];
  truncated: boolean;
}

/**
 * One exact word-count move shared by most of the pages that changed — a
 * template or footer edit, not that many rewrites. Surfaced so a screen full of
 * movement can be read as the single change it is.
 */
export interface CrawlRunSiteTemplateShift {
  wordCountChange: number;
  urls: number;
  share: number;
}

export interface CrawlRunSiteDiff {
  state: RunComparisonState;
  reason: string | null;
  headline: string;
  currentRun: CrawlRunRef | null;
  previousRun: CrawlRunRef | null;
  counts: Record<SiteDiffBucket, number>;
  urlsCompared: number;
  buckets: CrawlRunSiteDiffBucketView[];
  templateShift: CrawlRunSiteTemplateShift | null;
  notes: string[];
}

export interface CrawlRunSiteComparisonPayload {
  scopeDomain: string;
  runsCaptured: number;
  /** Newest first, ordered by the frozen `previousCrawlRunId` chain. */
  runs: CrawlRunRef[];
  diff: CrawlRunSiteDiff | null;
}

@Injectable({ providedIn: 'root' })
export class CrawlRunHistoryService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  /**
   * `crawlRunId` selects which run is compared, never what it is compared
   * against — the left-hand side is always that run's frozen predecessor.
   */
  getForDomain(
    domain: string,
    crawlRunId?: string | null
  ): Observable<{ success: boolean; data: CrawlRunSiteComparisonPayload }> {
    const query = crawlRunId ? `?crawlRunId=${encodeURIComponent(crawlRunId)}` : '';
    return this.http.get<{ success: boolean; data: CrawlRunSiteComparisonPayload }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl-runs${query}`
    );
  }
}
